/**
 * Armazenamento de arquivos grandes (PLAN §16).
 *
 * Camada separada do IndexedDB de propósito: vídeos têm ciclo de vida próprio,
 * podem somar gigabytes e não devem ser carregados inteiros em memória para serem
 * gravados. Ver ADR `docs/decisions/0001-offline-storage.md`.
 */

export interface WriteOptions {
  expectedBytes?: number;
  checksum?: string;
  onProgress?: (progress: { bytesWritten: number; totalBytes?: number }) => void;
  signal?: AbortSignal;
}

export interface OfflineFileStore {
  /** Identificador da implementação, para diagnóstico. */
  readonly id: string;

  isSupported(): boolean;

  /**
   * Grava um arquivo.
   *
   * A implementação escreve primeiro em `temp/` e só promove para `media/` ao
   * concluir (F12-T08, F12-T09). Uma interrupção deixa lixo em `temp/`, nunca um
   * arquivo pela metade em `media/` sendo dado como pronto.
   */
  write(key: string, source: Response | Blob, options?: WriteOptions): Promise<void>;

  exists(key: string): Promise<boolean>;
  read(key: string): Promise<Blob>;
  remove(key: string): Promise<void>;
  getSize(key: string): Promise<number | null>;
  list(): Promise<string[]>;

  /** Soma dos tamanhos de todos os arquivos gravados. */
  totalSize(): Promise<number>;

  /** Descarta temporários deixados por downloads interrompidos (F20-T07). */
  clearTemp(): Promise<number>;
}

/** Erro de espaço esgotado, normalizado entre as implementações. */
export class StorageQuotaError extends Error {
  constructor(message = 'Espaço de armazenamento esgotado.', options?: { cause?: unknown }) {
    super(message, options);
    this.name = 'StorageQuotaError';
  }
}

export class StorageWriteError extends Error {
  constructor(message: string, options?: { cause?: unknown }) {
    super(message, options);
    this.name = 'StorageWriteError';
  }
}

/**
 * Reconhece esgotamento de cota em qualquer navegador.
 *
 * O nome do erro varia: `QuotaExceededError` no padrão, `NS_ERROR_DOM_QUOTA_REACHED`
 * no Firefox, e o Safari às vezes só traz a mensagem.
 */
export function isQuotaError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;

  const name = error.name;
  if (name === 'QuotaExceededError' || name === 'NS_ERROR_DOM_QUOTA_REACHED') return true;

  return /quota|storage.*full|not enough space/i.test(error.message);
}

/** Converte a chave lógica (id do sinal) no nome do arquivo (PLAN §38). */
export function fileNameFor(key: string): string {
  // Só o ID do sinal, nunca o nome visível: acentos, renomeação e homônimos
  // tornariam o nome instável e colidente.
  return `${key}.mp4`;
}

export function keyFromFileName(fileName: string): string {
  return fileName.replace(/\.mp4$/, '');
}
